import React, { useState } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  FormControl,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { FactoringStatus } from "components/FactoringStatus";
import { statusToJapanese, type FactoringStatusType } from "components/FactoringStatus";
import type { Factoring } from "components/factoringModel";
import { getJaDisplayName } from "components/factoringUtil";
import { updateFactoringDetail } from "./updateFactoringDetail";

interface Props {
  factoring: Factoring;
  refresh: () => void;
}

export default function FactoringComponent(props: Props) {
  const {
    factoring: initialFactoring,
    refresh,
  } = props;

  // State for each field
  const [factoring, setFactoring] = useState<Factoring>(initialFactoring);

  // Loading state
  const [isLoading, setIsLoading] = useState(false);

  // Toast state
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Handle form field changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFactoring({
      ...factoring,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // Handle update button click
  const handleUpdate = async () => {
    if (!window.confirm("申込情報を更新しますか？")) {
      return;
    }

    setIsLoading(true);
    try {
      await updateFactoringDetail({
        userId: factoring.userId,
        factoringId: factoring.id,
        desiredAmount: factoring.desiredAmount,
        notes: factoring.notes,
        notification: factoring.notification,
        notificationDate: factoring.notificationDate,
        requireAdditionalImages: factoring.requireAdditionalImages,
        applicationNumber: factoring.applicationNumber,
        applicationDate: factoring.applicationDate,
        status: factoring.status,
      });
      refresh();
      setShowSuccessToast(true);
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "申込情報の更新中にエラーが発生しました。"
      );
      setShowErrorToast(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Toast close handlers
  const handleCloseSuccessToast = () => {
    setShowSuccessToast(false);
  };

  const handleCloseErrorToast = () => {
    setShowErrorToast(false);
  };

  return (
    <>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          ファクタリング申込詳細
        </Typography>

        <Grid container spacing={3} sx={{ mb: 5 }}>
          {/* Basic Information */}
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("applicationNumber")}
              name="applicationNumber"
              value={factoring.applicationNumber}
              onChange={handleChange}
              margin="normal"
              slotProps={{
                input: {
                  readOnly: true,
                },
              }}
              disabled={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("applicationDate")}
              name="applicationDate"
              type="date"
              value={factoring.applicationDate}
              onChange={handleChange}
              margin="normal"
              slotProps={{
                input: {
                  readOnly: true,
                },
              }}
              disabled={isLoading}
            />
          </Grid>

          {/* Application Information */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              申込情報
            </Typography>
            <Divider />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("desiredAmount")}
              name="desiredAmount"
              value={factoring.desiredAmount.toString()}
              onChange={(e) => setFactoring((oldVal) => {
                const onlyDigits = e.target.value.replace(/\D+/g, "");
                const num = onlyDigits ? Number(onlyDigits) : 0;
                return {
                  ...oldVal,
                  desiredAmount: num,
                }
              })}
              margin="normal"
              disabled={isLoading}
              slotProps={{
                htmlInput: {
                  pattern: "[0-9]+",
                  inputMode: 'numeric'
                },
                input: {
                  endAdornment: <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>円</Typography>,
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 12 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("notes")}
              name="notes"
              value={factoring.notes}
              onChange={handleChange}
              margin="normal"
              multiline
              rows={4}
              disabled={isLoading}
            />
          </Grid>

          {/* Notification */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              お知らせ
            </Typography>
            <Divider />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("notification")}
              name="notification"
              value={factoring.notification}
              onChange={handleChange}
              margin="normal"
              multiline
              rows={4}
              disabled={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={getJaDisplayName("notificationDate")}
              name="notificationDate"
              value={factoring.notificationDate}
              onChange={handleChange}
              margin="normal"
              disabled={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={factoring.requireAdditionalImages}
                  onChange={(e) => {
                    setFactoring((prev) => { return {
                      ...prev,
                      requireAdditionalImages: e.target.checked,
                    }});
                  }}
                  name="requireAdditionalImages"
                  disabled={isLoading}
                />
              }
              label={getJaDisplayName("requireAdditionalImages") || "追加画像が必要"}
            />
          </Grid>

          {/* Status */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              ステータス
            </Typography>
            <Divider />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth margin="normal">
              <Select
                id="status"
                name="status"
                value={factoring.status}
                onChange={(e) => {
                  setFactoring((prev) => { return {
                    ...prev,
                    status: e.target.value as FactoringStatusType,
                  }});
                }}
                disabled={isLoading}
              >
                {Object.entries(statusToJapanese).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    <FactoringStatus status={value as FactoringStatusType}>
                      {label}
                    </FactoringStatus>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Update Button */}
          <Grid size={{ xs: 12 }}>
            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpdate}
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} /> : null}
              >
                {isLoading ? "更新中..." : "更新"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Success Toast */}
      <Snackbar
        open={showSuccessToast}
        autoHideDuration={6000}
        onClose={handleCloseSuccessToast}
      >
        <Alert
          onClose={handleCloseSuccessToast}
          severity="success"
          sx={{ width: "100%" }}
        >
          申込情報を正常に更新しました。
        </Alert>
      </Snackbar>

      {/* Error Toast */}
      <Snackbar
        open={showErrorToast}
        autoHideDuration={6000}
        onClose={handleCloseErrorToast}
      >
        <Alert
          onClose={handleCloseErrorToast}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
